import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useMsal } from '@azure/msal-react';

import { PayLinkCartProvider } from '../context/PayLinkCartContext';
import { PayLinkUserProvider } from '../context/PaylinkUserContext';
import { Nav } from './Nav';

const INACTIVITY_30_MIN_MS = 30 * 60 * 1000;
const INACTIVITY_60_MIN_MS = 60 * 60 * 1000;

const LAST_ACTIVITY_KEY = "paylink_last_activity_at";
const AUTH_TIMEOUT_HANDLED_KEY = "paylink_auth_timeout_handled_at";
const TIMEOUT_BROADCAST_KEY = "paylink_timeout_broadcast";

const PAYLINK_LOCAL_STORAGE_KEYS = [
  "paylink_cart_items",
  "paylink_c_s",
  "C_S",
  "P_D",
  "session_data",
  "loginFlow",
  "mode",
  "redirectUrl",
  LAST_ACTIVITY_KEY,
  AUTH_TIMEOUT_HANDLED_KEY,
];

const getStorePathFromStorage = () => {
  const currentPath = window.location.pathname;
  const isCurrentStorePath = /^\/paylink\/s\/[^/]+(\/p\/[^/]+)?$/.test(currentPath);

  if (isCurrentStorePath) {
    return currentPath;
  }

  const entryPath = localStorage.getItem("paylink_entry_path") ?? "";
  const isStorePath = /^\/paylink\/s\/[^/]+(\/p\/[^/]+)?$/.test(entryPath);

  if (isStorePath) {
    return entryPath;
  }

  try {
    const storedProfile = localStorage.getItem("paylink_store_profile");
    if (storedProfile) {
      const profile = JSON.parse(storedProfile);
      if (profile?.slug) {
        return `/paylink/s/${profile.slug}`;
      }
    }
  } catch {
    // Ignore malformed profile data and fall back below.
  }

  return "/paylink/session";
};

const clearAuthOnlyStorage = () => {
  localStorage.removeItem("loginFlow");
  localStorage.removeItem("mode");
  localStorage.removeItem("redirectUrl");
  localStorage.removeItem("paylink_c_s");
};

const clearHardExpiredStorage = () => {
  const storePath = getStorePathFromStorage();
  let merchantSessionKey = "";

  try {
    const storedProfile = localStorage.getItem("paylink_store_profile");
    if (storedProfile) {
      const profile = JSON.parse(storedProfile);
      merchantSessionKey = profile?.customerUniqueReferenceNumber || "";
    }
  } catch {
    // Ignore malformed profile data.
  }

  PAYLINK_LOCAL_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
  });

  if (merchantSessionKey) {
    localStorage.removeItem(merchantSessionKey);
  }

  return storePath;
};

const PayLinkSessionManager: React.FC = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const timeoutInProgressRef = React.useRef(false);

  const ensureActivityTimestamp = React.useCallback(() => {
    const existing = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
    if (!existing) {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    }
  }, []);

  const markActivity = React.useCallback(() => {
    const now = Date.now();
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    localStorage.removeItem(AUTH_TIMEOUT_HANDLED_KEY);
  }, []);

  const performAuthTimeout = React.useCallback(async () => {
    if (timeoutInProgressRef.current) return;
    timeoutInProgressRef.current = true;

    const now = Date.now();
    const storePath = getStorePathFromStorage();
    clearAuthOnlyStorage();
    localStorage.setItem(AUTH_TIMEOUT_HANDLED_KEY, String(now));
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    localStorage.setItem(
      TIMEOUT_BROADCAST_KEY,
      JSON.stringify({ type: "auth-timeout", at: now, storePath }),
    );

    const account = accounts?.[0];
    if (account) {
      await instance.logoutRedirect({
        account,
        postLogoutRedirectUri: `${window.location.origin}/logout`,
      });
      return;
    }

    navigate(storePath, { replace: true });
    timeoutInProgressRef.current = false;
  }, [accounts, instance, navigate]);

  const performHardTimeout = React.useCallback(async () => {
    if (timeoutInProgressRef.current) return;
    timeoutInProgressRef.current = true;

    const now = Date.now();
    const storePath = clearHardExpiredStorage();
    localStorage.setItem(
      TIMEOUT_BROADCAST_KEY,
      JSON.stringify({ type: "hard-timeout", at: now, storePath }),
    );

    const account = accounts?.[0];
    if (account) {
      await instance.logoutRedirect({
        account,
        postLogoutRedirectUri: `${window.location.origin}/logout`,
      });
      return;
    }

    navigate(storePath, { replace: true });
    timeoutInProgressRef.current = false;
  }, [accounts, instance, navigate]);

  const evaluateTimeout = React.useCallback(() => {
    if (!pathname.startsWith("/paylink")) return;

    ensureActivityTimestamp();

    const now = Date.now();
    const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || now);
    const authHandledAt = Number(localStorage.getItem(AUTH_TIMEOUT_HANDLED_KEY) || 0);
    const idleMs = now - lastActivity;

    if (idleMs >= INACTIVITY_60_MIN_MS) {
      void performHardTimeout();
      return;
    }

    if (idleMs >= INACTIVITY_30_MIN_MS && accounts.length > 0 && authHandledAt < lastActivity) {
      void performAuthTimeout();
    }
  }, [accounts.length, ensureActivityTimestamp, pathname, performAuthTimeout, performHardTimeout]);

  React.useEffect(() => {
    // Refresh the activity timestamp unconditionally on mount (fresh page load or navigation mount)
    // so that previous session inactivity doesn't immediately trigger timeout on load.
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));

    ensureActivityTimestamp();
    evaluateTimeout();

    const activityEvents: Array<keyof WindowEventMap> = [
      "click",
      "keydown",
      "touchstart",
      "scroll",
      "mousemove",
      "focus",
    ];

    const handleActivity = () => {
      markActivity();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        evaluateTimeout();
      }
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== TIMEOUT_BROADCAST_KEY || !event.newValue) return;

      try {
        const parsed = JSON.parse(event.newValue);
        if (parsed?.type === "auth-timeout") {
          navigate(parsed.storePath || "/paylink/session", { replace: true });
        }
        if (parsed?.type === "hard-timeout") {
          navigate(parsed.storePath || "/paylink/session", { replace: true });
        }
      } catch {
        // Ignore malformed storage payload.
      }
    };

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    window.addEventListener("storage", handleStorage);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const intervalId = window.setInterval(() => {
      evaluateTimeout();
    }, 60 * 1000);

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [ensureActivityTimestamp, evaluateTimeout, markActivity, navigate]);

  return null;
};

interface IProps {
  children?: React.ReactNode;
}

export const PayLinkBaseLayout: React.FC<IProps> = ({ children }) => {
  return (
    <React.Fragment>
      <PayLinkUserProvider>
        <PayLinkCartProvider>
          <PayLinkSessionManager />
          <Nav />
          <section className="w-full min-h-[calc(100vh-56px)] bg-white">
            {children}
            <Outlet />
          </section>
        </PayLinkCartProvider>
      </PayLinkUserProvider>
    </React.Fragment>
  );
};
